/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { createDialogRoute, getDialogInitData } from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import {
  DialogInput,
  NotificationService,
  responsiveDialogOverlayPaneClass,
  WaitingService,
} from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';
import {
  LicenseAgreementDialogBusinessService,
  LicenseAgreementDialogConfig,
  UserAgreement,
} from '../../resources/license-agreement-dialog-business.service';

export const IS_MS_BROWSER = isNotNil(window.navigator.msSaveBlob);

const LICENSE_AGREEMENT_HEAD_HTML = [
  '<link rel="stylesheet" type="text/css" id="u1" href="https://fonts.googleapis.com/css?family=Roboto">',
  '<style type="text/css">',
  `body {
       padding: 30px 30px 0 30px;
       overflow-x: hidden;

       font-family: 'Roboto';
       font-size: 15px;
       
       @media (min-width: 600px) {
         width: 360px;
       }
    }`,
  '</style>',
].join('');

@Component({
  standalone: false,
  selector: 'gh-license-agreement-dialog',
  templateUrl: 'license-agreement-dialog.component.html',
  styleUrls: ['license-agreement-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LicenseAgreementDialogComponent implements OnInit, AfterViewInit, OnDestroy, DialogInput<LicenseAgreementDialogConfig> {
  input: LicenseAgreementDialogConfig;

  agreement: UserAgreement;

  @ViewChild('eulaContent', { read: ElementRef, static: false }) eulaContent: ElementRef;
  @ViewChild('eulaLink', { read: ElementRef, static: false }) eulaLink: ElementRef;

  eulaDocumentDownloadUrl: string;
  isMsBrowser = IS_MS_BROWSER;

  constructor(@Inject(MAT_DIALOG_DATA) private dialogData: any,
              private dialogRef: MatDialogRef<any>,
              private waitings: WaitingService,
              private notifications: NotificationService,
              private translations: TranslationService,
              private domSanitizer: DomSanitizer,
              private business: LicenseAgreementDialogBusinessService) {
  }

  ngOnInit(): void {
    const { agreement } = getDialogInitData(this.dialogData);
    this.agreement = agreement;
  }

  ngAfterViewInit(): void {
    const { eulaDocument } = getDialogInitData(this.dialogData);

    const iframe = this.eulaContent.nativeElement;
    iframe.contentDocument.head.innerHTML = LICENSE_AGREEMENT_HEAD_HTML;
    iframe.contentDocument.body.innerHTML = this.agreement.eula;
    if (!IS_MS_BROWSER) {
      this.eulaDocumentDownloadUrl = URL.createObjectURL(eulaDocument);
      this.eulaLink.nativeElement.href = this.eulaDocumentDownloadUrl;
    }

    this.autoHeight();
  }

  ngOnDestroy(): void {
    if (this.eulaDocumentDownloadUrl) {
      URL.revokeObjectURL(this.eulaDocumentDownloadUrl);
    }
  }

  onAccept(): void {
    this.business.accept({ ...this.agreement, accepted: true }).pipe(
      this.waitings.operator())
      .subscribe(
        () => this.dialogRef.close(true),
        this.notifications.processAll);
  }

  onDecline(): void {
    this.dialogRef.close(false);
  }

  onClose(): void {
    if (this.input.viewMode) {
      this.dialogRef.close();
    }
  }

  onDownload(): void {
    if (IS_MS_BROWSER && window.navigator.msSaveBlob) {
      const { eulaDocument } = getDialogInitData(this.dialogData);
      window.navigator.msSaveBlob(eulaDocument, `${this.translations.get('lbl_license_agreement')}.pdf`);
    }
  }

  private autoHeight(): void {
    const iframeEl = this.eulaContent.nativeElement;
    iframeEl.style.height = `${iframeEl.contentWindow.document.body.scrollHeight}px`;
  }
}

export const licenseAgreementDialogRoute = createDialogRoute({
  component: LicenseAgreementDialogComponent,
  init: LicenseAgreementDialogBusinessService,
  config: {
    disableClose: true,
    autoFocus: false,
    panelClass: responsiveDialogOverlayPaneClass(),
  },
});
